import type { LicenceResponse } from '@shared/schemas';
import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects';

import type { LicenceDraft } from '../domain/licence-draft.model';
import { validateDraft } from '../domain/licence-draft.validation';
import { toMyLicenceError } from '../domain/my-licence-error';
import type { MyLicenceGateway } from '../infrastructure/my-licence.gateway';
import { loadRequested, submitRequested } from './my-licence.actions';
import { selectDraft, selectLicence } from './my-licence.selectors';
import { myLicenceActions } from './my-licence.slice';

/**
 * WORKER: hydrate the owner's current licence. All I/O goes through the injected
 * gateway (read from the saga `context`), so tests drive the success / api-error /
 * network-error branches with an in-memory gateway.
 */
function* loadWorker() {
  const gateway: MyLicenceGateway = yield getContext('myLicenceGateway');
  yield put(myLicenceActions.loadStarted());
  try {
    const licence = (yield call(gateway.loadCurrentLicence)) as LicenceResponse;
    yield put(myLicenceActions.loadSucceeded(licence));
  } catch (e) {
    yield put(myLicenceActions.loadFailed(toMyLicenceError(e)));
  }
}

/**
 * WORKER: persist the renamed licence. Validates first (pure domain); on failure
 * dispatches a `validation` error and stops without touching the gateway.
 * `takeLatest` means a fresh submit supersedes any in-flight save.
 */
function* saveWorker() {
  const licence = (yield select(selectLicence)) as LicenceResponse | null;
  if (!licence) return;
  const draft = (yield select(selectDraft)) as LicenceDraft;

  const errors = validateDraft(draft);
  if (Object.keys(errors).length > 0) {
    yield put(myLicenceActions.saveFailed({ kind: 'validation', fields: errors }));
    return;
  }

  const gateway: MyLicenceGateway = yield getContext('myLicenceGateway');
  yield put(myLicenceActions.saveStarted());
  try {
    const updated = (yield call(
      gateway.updateLicence,
      licence.id,
      draft.name.trim(),
    )) as LicenceResponse;
    yield put(myLicenceActions.saveSucceeded(updated));
  } catch (e) {
    yield put(myLicenceActions.saveFailed(toMyLicenceError(e)));
  }
}

/** WATCHERS. */
export function* myLicenceSaga() {
  yield all([
    takeLatest(loadRequested.type, loadWorker),
    takeLatest(submitRequested.type, saveWorker),
  ]);
}
